import * as React from "react";
import { Send } from "lucide-react";
import { Field, RadioGroup, Label } from "@/components/ui";
import { VideoProjectStatus, VideoProjectDetail, SocialNetworksId } from "@/interfaces";
import { SocialNetworkIconButton } from "@/components/SocialNetworkIconButton";
import { ProjectScheduler } from "@/components/Forms/VideoProject/components/ProjectScheduler";

interface Props {
  videoProject: VideoProjectDetail;
  disabled: boolean;
  postableSocialNetworks: string[];
  onChangeField: (field: keyof VideoProjectDetail, value: any) => void;
}

export const EditorPostingPreferences: React.FC<Props> = ({
  videoProject,
  disabled,
  postableSocialNetworks,
  onChangeField,
}) => {
  const isAutoPost = !!videoProject.isAutoPost;

  return (
    <Field className="flex flex-col gap-1.5">
      <Field.Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
        <Send className="size-3.5 text-slate-400" />
        Publishing Method
      </Field.Label>
      <Field.Content className="mt-2">
        <RadioGroup
          value={isAutoPost ? "autoPost" : "pushNotification"}
          onValueChange={val => {
            onChangeField("isAutoPost", val === "autoPost");
          }}
          className="flex flex-col gap-5 pl-4"
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center space-x-2">
              <RadioGroup.Item value="autoPost" id="autoPost" />
              <Label htmlFor="autoPost" className="text-slate-800 font-semibold cursor-pointer font-sans">
                Auto Post
              </Label>
            </div>

            {isAutoPost && (
              <div className="pl-6 flex flex-col gap-4 mt-1">
                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Select Social Networks
                  </span>
                  <div className="flex flex-row flex-wrap items-center gap-2.5">
                    {postableSocialNetworks.map((sn, index) => {
                      return (
                        <SocialNetworkIconButton
                          disabled={disabled}
                          size="noMargin"
                          key={"video_project_post_sn_icon_" + index}
                          active={videoProject.socialNetworks ? videoProject.socialNetworks.includes(sn as any) : false}
                          networkId={sn as SocialNetworksId}
                          customIconClassname="socialNetworkIconCentered"
                          onClickHandler={checked => {
                            let snUpdated: any[] = videoProject.socialNetworks || [];
                            if (checked) {
                              snUpdated = snUpdated.concat([sn]);
                            } else {
                              snUpdated = snUpdated.filter((p: string) => p !== sn);
                            }
                            onChangeField("socialNetworks", snUpdated);
                          }}
                        />
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Schedule Time</span>
                  <div className="w-full bg-white border border-slate-200 p-4 rounded-xl shadow-xs">
                    <ProjectScheduler
                      deliveryDate={videoProject.preferredPostDate || new Date().toISOString()}
                      editable={videoProject.status !== VideoProjectStatus.POSTED}
                      minDate={new Date()}
                      onChange={(date: Date) => {
                        onChangeField("preferredPostDate", date.toISOString());
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            <RadioGroup.Item value="pushNotification" id="pushNotification" />
            <Label htmlFor="pushNotification" className="text-slate-900 font-semibold cursor-pointer">
              Push Notification
            </Label>
          </div>
        </RadioGroup>
      </Field.Content>
    </Field>
  );
};
